import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { LOGO_URL } from "@/lib/content";

export default function Navbar() {
  const { c, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangToggle = ({ className = "" }) => (
    <div data-testid="lang-toggle" className={`flex items-center border border-cream/60 ${className}`}>
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`font-sans text-[11px] font-semibold uppercase tracking-[0.14em] px-3 py-1.5 transition-colors duration-300 ${
            lang === l ? "bg-gold text-forest" : "text-cream hover:text-gold [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-forest/85 backdrop-blur-xl border-b border-gold/20" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] pl-0 pr-4 sm:pr-8 h-[80px] sm:h-[104px] lg:h-[128px] flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="group flex items-center select-none -ml-1 sm:-ml-2">
          <img
            src="/logo-transparent.png"
            alt="Green Gold"
            className="h-[56px] sm:h-[88px] lg:h-[120px] w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </a>

        <div className="hidden md:flex items-center gap-9">
          {c.nav.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="link-underline font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-cream hover:text-gold py-1 [text-shadow:0_1px_4px_rgba(0,0,0,0.55)]"
            >
              {l.label}
            </a>
          ))}
          <LangToggle />
          <a
            href="#contact"
            data-testid="nav-cta"
            className="whitespace-nowrap font-sans text-[12px] uppercase tracking-[0.18em] border border-gold text-gold px-5 py-2.5 hover:bg-gold hover:text-forest transition-colors duration-400"
          >
            {c.navCta}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="mobile-menu-toggle"
            className="text-cream p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-forest/95 backdrop-blur-xl border-b border-gold/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {c.nav.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-sans text-sm uppercase tracking-[0.18em] text-cream/80"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="mobile-nav-cta"
                onClick={() => setOpen(false)}
                className="mt-2 text-center font-sans text-xs uppercase tracking-[0.18em] border border-gold text-gold px-5 py-3 hover:bg-gold hover:text-forest transition-colors duration-400"
              >
                {c.navCta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
